import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Phone, MessageCircle, Zap, AlertTriangle } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const BlogSalvavitaScattaCosaFare = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Salvavita Scatta Cosa Fare a Roma - Guida Urgente | SOS Casa Roma</title>
        <meta name="description" content="Salvavita che scatta a Roma? Scopri cosa fare immediatamente. Chiamaci H24 per intervento urgente entro 60 minuti. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/blog/salvavita-scatta-cosa-fare`} />
      </Helmet>
      
      <div className="min-h-screen">
        <Header />
        
        <article className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-yellow-100 border-l-4 border-yellow-600 p-4 mb-6">
              <div className="flex items-center">
                <Zap className="text-yellow-600 mr-2" />
                <p className="font-bold text-yellow-800">EMERGENZA ELETTRICA? Chiamaci subito: 328 768 6356</p>
              </div>
            </div>

            <h1 className="text-4xl font-bold mb-6 text-gray-900">
              Salvavita Scatta: Cosa Fare Subito a Roma
            </h1>
            
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 mb-6">
                Il tuo <strong>salvavita scatta continuamente</strong> e rimani al buio? 
                Segui questa guida passo-passo:
              </p>

              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">1️⃣ Scollega Tutti gli Elettrodomestici</h2>
                <p className="text-gray-700 mb-3">
                  <strong>Prima di riattivare il salvavita</strong>, stacca dalle prese:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Frigorifero e congelatore</li>
                  <li>Lavatrice e lavastoviglie</li>
                  <li>Forno e microonde</li>
                  <li>Televisori e computer</li>
                </ul>
                <p className="text-sm text-gray-600 mt-2">Questo aiuta a capire quale apparecchio causa il problema.</p>
              </div>

              <div className="bg-orange-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">2️⃣ Riattiva il Salvavita</h2>
                <p className="text-gray-700 mb-3">
                  Vai al <strong>quadro elettrico</strong> e alza la levetta del salvavita (differenziale).
                </p>
                <p className="text-gray-700 mb-3">
                  <strong>Se scatta immediatamente:</strong> C'è un problema grave all'impianto. NON insistere, chiama un elettricista subito.
                </p>
                <p className="text-gray-700">
                  <strong>Se rimane attaccato:</strong> Riattacca gli elettrodomestici uno alla volta per trovare quello difettoso.
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">3️⃣ Chiama Immediatamente un Elettricista</h2>
                <p className="text-gray-700 mb-4">
                  Se il salvavita continua a scattare, c'è un <strong>problema elettrico serio</strong>. Non rischiare.
                </p>
                <div className="bg-white rounded-lg p-4 border-2 border-red-500">
                  <p className="font-bold text-red-800 mb-2">⚡ SOS Casa Roma - Elettricista Urgente H24</p>
                  <p className="text-gray-700 mb-2">✅ Tecnici certificati DM 37/08</p>
                  <p className="text-gray-700 mb-4">✅ Arriviamo entro 60 minuti a Roma</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href="tel:3287686356" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <Phone className="mr-2 h-5 w-5" />
                      CHIAMA ORA: 328 768 6356
                    </a>
                    <a href="https://wa.me/393287686356" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <MessageCircle className="mr-2 h-5 w-5" />
                      SCRIVI SU WHATSAPP
                    </a>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Perché Scatta il Salvavita?</h3>
              <p className="text-gray-700 mb-4">
                Il salvavita (interruttore differenziale) scatta per <strong>proteggere le persone</strong> da scosse elettriche. 
                Quando rileva una dispersione di corrente, si spegne automaticamente.
              </p>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Cause Più Comuni a Roma:</h3>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li className="mb-2"><strong>Elettrodomestico difettoso:</strong> Lavatrice, frigo, forno vecchi</li>
                <li className="mb-2"><strong>Infiltrazione d'acqua:</strong> Umidità in una presa o scatola di derivazione</li>
                <li className="mb-2"><strong>Cavi danneggiati:</strong> Fili scoperti o rosicchiati da topi</li>
                <li className="mb-2"><strong>Sovraccarico impianto:</strong> Troppi apparecchi accesi contemporaneamente</li>
                <li className="mb-2"><strong>Salvavita vecchio o difettoso:</strong> Va sostituito</li>
              </ul>

              <div className="bg-yellow-50 border-2 border-yellow-500 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">⚠️ Quando è URGENTE chiamare:</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Salvavita scatta e NON si riattacca</li>
                  <li>Scintille o odore di bruciato dalle prese</li>
                  <li>Salvavita scatta quando piove</li>
                  <li>Salvavita scatta di notte senza motivo apparente</li>
                </ul>
                <p className="mt-4 font-bold text-red-700">In questi casi NON toccare nulla. Chiama immediatamente un elettricista certificato.</p>
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Cosa NON Fare Mai:</h3>
              <div className="bg-red-50 rounded-lg p-4 mb-6">
                <ul className="list-disc pl-6 text-red-700">
                  <li className="mb-2"><strong>NON bypassare</strong> il salvavita con ponticelli o fili</li>
                  <li className="mb-2"><strong>NON usare</strong> nastro isolante su cavi danneggiati</li>
                  <li className="mb-2"><strong>NON tenere attaccato</strong> il salvavita con la forza</li>
                </ul>
                <p className="mt-2 font-bold">Queste azioni sono PERICOLOSE e possono causare incendi o folgorazione.</p>
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Quanto Costa un Intervento Elettrico a Roma?</h3>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-gray-700 mb-2">• <strong>Uscita tecnico:</strong> 40€</p>
                <p className="text-gray-700 mb-2">• <strong>Intervento entro 1 ora:</strong> 60€</p>
                <p className="text-gray-700 mb-2">• <strong>Emergenza notturna:</strong> da 80€</p>
                <p className="text-sm text-gray-600 mt-2">Riparazione inclusa • Certificazione DM 37/08 inclusa</p>
              </div>

              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Salvavita Scatta Adesso?</h3>
                <p className="text-lg text-gray-700 mb-4">Non restare al buio. Chiamaci subito.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="tel:3287686356" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <Phone className="mr-2 h-6 w-6" />
                      CHIAMA ORA: 328 768 6356
                    </a>
                  <a href="https://wa.me/393287686356" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <MessageCircle className="mr-2 h-6 w-6" />
                      WHATSAPP IMMEDIATO
                    </a>
                </div>
                <p className="text-sm text-gray-600 mt-4">✅ Intervento H24 • ✅ Certificati DM 37/08 • ✅ Prezzi chiari</p>
              </div>
            </div>
          </div>
        </article>
        
        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default BlogSalvavitaScattaCosaFare;